import { isOperationalTask } from './managerFilters'
import { getAttentionTasks, getAttentionLabel, getControlTasks, getWaitingStatusLabel } from './managerPresentation'
import {
  api,
  translateStatus,
  type Document,
  type ManagerControlTask,
  type ManagerControlTaskCode,
  type Shipment,
} from './api'
import { describeAutoCompletion } from './completionRequest'
import { formatRelativeTrackingTimestamp } from './relativeTrackingTime'
import { extractCitiesFromRoute, getShortRoutePointLabel } from './routeUtils'
import { parseRouteData as parseRouteJson } from './routeData'

export { isAttentionTask, getAttentionTasks, getAttentionLabel, isControlTask, getControlTasks } from './managerPresentation'

export type ManagerRouteData = {
  order_number?: string
  vehicle_type?: string
  driver_name?: string
  company?: string
  contact?: string
  driver_phone?: string
  vehicle_number?: string
  trailer_number?: string
  route?: string
  points?: Array<{ address?: string }>
  loading_date?: string
  unloading_date?: string
}

export type TrackingSnapshot = {
  hasPositions: boolean
  positionCount: number
  lastPositionAt?: string
}

export type CalendarEventType = 'loading' | 'unloading'

export type ShipmentCalendarEvent = {
  id: string
  shipmentId: string
  shipment: Shipment
  type: CalendarEventType
  date: Date
  dateKey: string
  hasTime: boolean
  route: string
  orderNumber: string
  driverName: string
}

// Control tasks are decided on the server (models.BuildManagerShipmentState) and arrive with every
// shipment (shipment.operational) and, for the whole queue, in the operational summary. This module
// only turns them into display items — it never decides what is active, late or missing.

export type AttentionCode = ManagerControlTaskCode

// The shipment's most urgent task, for highlighting its card.
export type AttentionItem = {
  id: string
  code: AttentionCode
  title: string
  route: string
  statusCode: string
  status: string
  reason: string
  priority: number
  createdAt: string
}

export type ManagerTaskCode = ManagerControlTaskCode

export function isDriverInvitationTask(task: { code: ManagerTaskCode }) {
  return task.code === 'missing_assignment' || task.code === 'missing_vehicle'
    || task.code === 'missing_driver' || task.code === 'driver_rejected'
}

export type ManagerTaskPriority = 'high' | 'medium'

export type ManagerTaskItem = {
  id: string
  shipmentId: string
  code: ManagerTaskCode
  title: string
  route: string
  statusCode: string
  status: string
  reason: string
  priority: ManagerTaskPriority
  priorityRank: number
  createdAt: string
  // A manager can consciously close only informational follow-ups. Missing assignment and
  // live-tracking problems remain actionable until the underlying situation is fixed.
  canBeManuallyResolved: boolean
  // The server's verdict: a problem, not just the next preparation step.
  attention: boolean
  // Listed in «Контроль перевозок» (the server's `control`; attention when the answer predates it).
  control: boolean
}

export function isCompletedStatus(status: string) {
  return status === 'completed' || status === 'delivered'
}

export function isCancelledStatus(status: string) {
  return status === 'cancelled' || status === 'rejected'
}

// Mirrors models.IsActiveEffectiveStatus on the server: a driver's refusal is active too — only the
// manager closes a shipment, the refusal just frees it for another driver. Use it only for a bare
// status; for a shipment prefer isShipmentActive, which reads the server's own decision.
export function isActiveStatus(status: string) {
  return status === 'created'
    || status === 'pending'
    || status === 'assigned'
    || status === 'awaiting_driver'
    || status === 'driver_rejected'
    || status === 'accepted'
    || status === 'in_progress'
}

export function getEffectiveShipmentStatus(shipment: Shipment) {
  return shipment.operational?.effective_status || shipment.effective_status || shipment.status
}

export function isShipmentClosed(shipment: Shipment) {
  const closed = (status: string) => isCompletedStatus(status) || isCancelledStatus(status)
  return closed(shipment.status) || closed(getEffectiveShipmentStatus(shipment))
}

export function canEditShipment(shipment: Shipment) {
  return !isShipmentClosed(shipment)
}

export function isShipmentActive(shipment: Shipment) {
  return shipment.operational ? shipment.operational.active : isActiveStatus(getEffectiveShipmentStatus(shipment))
}

export function isShipmentInTransit(shipment: Shipment) {
  return shipment.operational ? shipment.operational.in_transit : getEffectiveShipmentStatus(shipment) === 'in_progress'
}

export function getShipmentControlTasks(shipment: Shipment): ManagerControlTask[] {
  return shipment.operational?.control_tasks ?? []
}

export function parseRouteData(shipment: Shipment): ManagerRouteData | null {
  return parseRouteJson(shipment.route_json)
}

export function getRouteLabel(shipment: Shipment) {
  const routeData = parseRouteData(shipment)
  const points = routeData?.points?.filter((point) => point.address?.trim())
  if (points && points.length >= 2) {
    const fromAddress = points[0]?.address?.trim()
    const toAddress = points[points.length - 1]?.address?.trim()
    const from = fromAddress ? getShortRoutePointLabel(fromAddress) : ''
    const to = toAddress ? getShortRoutePointLabel(toAddress) : ''
    if (from && to) return `${from} → ${to}`
  }

  const route = routeData?.route ?? ''
  if (!route) return 'Маршрут не указан'

  const cities = extractCitiesFromRoute(route)
  return cities.length > 0 ? cities.join(' → ') : route
}

export function getShipmentTitle(shipment: Shipment) {
  const routeData = parseRouteData(shipment)
  const orderNumber = routeData?.order_number ?? ''
  return orderNumber ? `Заявка ${orderNumber}` : `Заявка #${shipment.id.slice(-8)}`
}

export function getCalendarDateKey(date: Date) {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

type CalendarDateValue = {
  date: Date
  hasTime: boolean
}

function parseCalendarDateValue(value: unknown): CalendarDateValue | null {
  if (typeof value !== 'string' && !(value instanceof Date) && typeof value !== 'number') {
    return null
  }

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return {
    date,
    // Calendar dates can be supplied without a time. Keep that distinction so the
    // dashboard does not invent a misleading "00:00" label for an all-day event.
    hasTime: value instanceof Date || typeof value === 'number' || /(?:T|\s)\d{1,2}:\d{2}/.test(value),
  }
}

function pickFirstCalendarDate(candidates: unknown[]): CalendarDateValue | null {
  for (const candidate of candidates) {
    const dateValue = parseCalendarDateValue(candidate)
    if (dateValue) return dateValue
  }

  return null
}

function getShipmentCalendarDateValues(shipment: Shipment) {
  const routeData = parseRouteData(shipment)
  const routeRecord = (routeData ?? {}) as Record<string, unknown>
  const shipmentRecord = shipment as Shipment & Record<string, unknown>

  const loadingDate = pickFirstCalendarDate([
    routeRecord.loading_date,
    routeRecord.loadingDate,
    routeRecord.pickup_date,
    routeRecord.pickupDate,
    routeRecord.load_date,
    routeRecord.loadDate,
    routeRecord.start_date,
    routeRecord.startDate,
    routeRecord.planned_start_date,
    routeRecord.plannedStartDate,
    shipmentRecord.loading_date,
    shipmentRecord.loadingDate,
    shipmentRecord.pickup_date,
    shipmentRecord.pickupDate,
    shipmentRecord.load_date,
    shipmentRecord.loadDate,
    shipmentRecord.start_date,
    shipmentRecord.startDate,
    shipmentRecord.planned_start_date,
    shipmentRecord.plannedStartDate,
    shipment.created_at,
  ])

  const unloadingDate = pickFirstCalendarDate([
    routeRecord.unloading_date,
    routeRecord.unloadingDate,
    routeRecord.delivery_date,
    routeRecord.deliveryDate,
    routeRecord.unload_date,
    routeRecord.unloadDate,
    routeRecord.finish_date,
    routeRecord.finishDate,
    routeRecord.end_date,
    routeRecord.endDate,
    routeRecord.planned_delivery_date,
    routeRecord.plannedDeliveryDate,
    routeRecord.planned_delivery_time,
    routeRecord.plannedDeliveryTime,
    routeRecord.planned_date,
    routeRecord.plannedDate,
    shipmentRecord.unloading_date,
    shipmentRecord.unloadingDate,
    shipmentRecord.delivery_date,
    shipmentRecord.deliveryDate,
    shipmentRecord.unload_date,
    shipmentRecord.unloadDate,
    shipmentRecord.finish_date,
    shipmentRecord.finishDate,
    shipmentRecord.end_date,
    shipmentRecord.endDate,
    shipmentRecord.planned_delivery_date,
    shipmentRecord.plannedDeliveryDate,
    shipmentRecord.planned_delivery_time,
    shipmentRecord.plannedDeliveryTime,
    shipment.planned_delivery_time,
  ])

  return { loadingDate, unloadingDate }
}

export function getShipmentCalendarDates(shipment: Shipment) {
  const { loadingDate, unloadingDate } = getShipmentCalendarDateValues(shipment)
  return {
    loadingDate: loadingDate?.date ?? null,
    unloadingDate: unloadingDate?.date ?? null,
  }
}

export function getShipmentCalendarEvents(shipment: Shipment): ShipmentCalendarEvent[] {
  const routeData = parseRouteData(shipment)
  const { loadingDate, unloadingDate } = getShipmentCalendarDateValues(shipment)
  const route = getRouteLabel(shipment)
  const orderNumber = routeData?.order_number || shipment.id.slice(-8)
  const driverName = routeData?.driver_name || ''
  const events: ShipmentCalendarEvent[] = []

  if (loadingDate) {
    const dateKey = getCalendarDateKey(loadingDate.date)
    events.push({
      id: `${shipment.id}:loading:${dateKey}`,
      shipmentId: shipment.id,
      shipment,
      type: 'loading',
      date: loadingDate.date,
      dateKey,
      hasTime: loadingDate.hasTime,
      route,
      orderNumber,
      driverName,
    })
  }

  if (unloadingDate) {
    const dateKey = getCalendarDateKey(unloadingDate.date)
    events.push({
      id: `${shipment.id}:unloading:${dateKey}`,
      shipmentId: shipment.id,
      shipment,
      type: 'unloading',
      date: unloadingDate.date,
      dateKey,
      hasTime: unloadingDate.hasTime,
      route,
      orderNumber,
      driverName,
    })
  }

  return events
}

export function buildShipmentCalendarEvents(shipments: Shipment[]) {
  return shipments.flatMap((shipment) => getShipmentCalendarEvents(shipment))
}

export function getUpcomingShipmentCalendarEvents(
  shipments: Shipment[],
  now: Date,
  limit = 5,
) {
  const todayKey = getCalendarDateKey(now)

  return buildShipmentCalendarEvents(shipments)
    .filter((event) => {
      if (event.dateKey > todayKey) return true
      if (event.dateKey < todayKey) return false

      // A date without a specified time remains relevant for the whole current day.
      return !event.hasTime || event.date.getTime() >= now.getTime()
    })
    .sort((left, right) => left.date.getTime() - right.date.getTime())
    .slice(0, limit)
}

export function buildDocumentsByShipment(documents: Document[]) {
  return documents.reduce((map, document) => {
    const current = map.get(document.shipment_id) ?? []
    current.push(document)
    map.set(document.shipment_id, current)
    return map
  }, new Map<string, Document[]>())
}

function normalizeTimestamp(timestamp: unknown) {
  if (!timestamp) return undefined
  if (typeof timestamp === 'string') {
    // Go zero-time sentinel — treat as no data
    if (timestamp.startsWith('0001-')) return undefined
    return timestamp
  }
  if (timestamp instanceof Date) return Number.isNaN(timestamp.getTime()) ? undefined : timestamp.toISOString()
  if (typeof timestamp === 'number') {
    // Go APIs normally send RFC 3339 strings. Keep numeric compatibility for older/public
    // payloads too: ten-digit Unix seconds and thirteen-digit JavaScript milliseconds are both
    // accepted, never silently turned into a date near 1970.
    const milliseconds = Math.abs(timestamp) < 100_000_000_000 ? timestamp * 1000 : timestamp
    const date = new Date(milliseconds)
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
  }
  return undefined
}

// buildTrackingFromShipments derives the tracking snapshots straight from the shipment summaries,
// whose last_location is resolved server-side and arrives with the list. Preferred for the manager
// dashboard: the data is present on the first render, so there is no loading flicker and no extra
// request. positionCount is only ever read as ">0", so a present fix maps to 1. Use
// fetchTrackingSnapshots when the full track (real position counts) is actually needed.
export function buildTrackingFromShipments(shipments: Shipment[]): Record<string, TrackingSnapshot> {
  const result: Record<string, TrackingSnapshot> = {}
  for (const shipment of shipments) {
    const lastPositionAt = normalizeTimestamp(shipment.last_location?.timestamp)
    result[shipment.id] = {
      hasPositions: Boolean(lastPositionAt),
      positionCount: lastPositionAt ? 1 : 0,
      lastPositionAt,
    }
  }
  return result
}

// The batch-snapshot endpoint caps one request at 50 ids; chunk so nothing is silently dropped.
const TRACKING_SNAPSHOT_BATCH_SIZE = 50

// fetchTrackingSnapshots pulls the full driven tracks for the given shipments via the manager
// batch endpoint (one request per 50 shipments instead of a per-shipment fan-out, and no
// public-token minting as a side effect of merely viewing the list).
export async function fetchTrackingSnapshots(shipments: Shipment[]): Promise<Record<string, TrackingSnapshot>> {
  // Every shipment starts as "no data"; batch responses overwrite the ones the server
  // returned. Absent from the response (not owned, transient failure) keeps the default.
  const result: Record<string, TrackingSnapshot> = {}
  for (const shipment of shipments) {
    result[shipment.id] = { hasPositions: false, positionCount: 0 }
  }

  const ids = shipments.map((shipment) => shipment.id)
  const batches: string[][] = []
  for (let i = 0; i < ids.length; i += TRACKING_SNAPSHOT_BATCH_SIZE) {
    batches.push(ids.slice(i, i + TRACKING_SNAPSHOT_BATCH_SIZE))
  }

  await Promise.all(
    batches.map(async (batch) => {
      try {
        const snapshots = await api.getTrackingSnapshots(batch)
        for (const snap of snapshots) {
          const positions = snap.positions || []
          result[snap.shipment_id] = {
            hasPositions: positions.length > 0,
            positionCount: positions.length,
            lastPositionAt: normalizeTimestamp(snap.last?.timestamp),
          }
        }
      } catch {
        // Failed batch: its shipments keep the "no data" default; the next refresh retries.
      }
    }),
  )

  return result
}

export function formatHoursSince(timestamp: string, now: Date) {
  return formatRelativeTrackingTimestamp(timestamp, now.getTime()) ?? 'недавно'
}

export function getLastTrackingLabel(
  shipment: Shipment,
  tracking: TrackingSnapshot | undefined,
  now: Date,
) {
  const status = getEffectiveShipmentStatus(shipment)

  if (isCompletedStatus(status)) {
    return 'Перевозка завершена'
  }

  if (tracking?.lastPositionAt) {
    return `Обновлено ${formatHoursSince(tracking.lastPositionAt, now)}`
  }

  if (status === 'in_progress') {
    return 'Геопозиция не поступала'
  }

  if (status === 'accepted') {
    // Accepted, «Начать рейс» not pressed: no position is expected yet.
    return 'Рейс не начат'
  }

  if (status === 'assigned' || status === 'pending' || status === 'awaiting_driver' || status === 'driver_rejected') {
    return 'Водитель ещё не начал трекинг'
  }

  return 'Ожидает старта перевозки'
}

function getShipmentActivityTimestamp(
  shipment: Shipment,
  trackingByShipment: Record<string, TrackingSnapshot>,
) {
  const trackingTimestamp = trackingByShipment[shipment.id]?.lastPositionAt
  const trackingTime = trackingTimestamp ? new Date(trackingTimestamp).getTime() : 0
  const createdTime = new Date(shipment.created_at).getTime()

  return Math.max(
    Number.isNaN(trackingTime) ? 0 : trackingTime,
    Number.isNaN(createdTime) ? 0 : createdTime,
  )
}

function getShipmentPriorityBucket(shipment: Shipment, attentionItem?: AttentionItem) {
  const status = getEffectiveShipmentStatus(shipment)
  if (attentionItem) return 0
  if (status === 'in_progress') return 1
  if (status === 'accepted' || status === 'created' || status === 'pending' || status === 'awaiting_driver') return 2
  if (status === 'assigned' || status === 'driver_rejected') return 3
  return 4
}

export function sortActiveShipmentsForDashboard(
  shipments: Shipment[],
  attentionByShipmentId: Record<string, AttentionItem>,
  trackingByShipment: Record<string, TrackingSnapshot>,
) {
  // Geolocation takes precedence over control tasks; use the server's decision when available.
  const hasTracking = (shipment: Shipment) => shipment.operational?.has_geolocation
    ?? Boolean(shipment.last_location?.timestamp || trackingByShipment[shipment.id]?.hasPositions)
  return [...shipments].sort((left, right) => {
    const trackingDifference = Number(hasTracking(right)) - Number(hasTracking(left))
    if (trackingDifference !== 0) return trackingDifference

    const leftAttention = attentionByShipmentId[left.id]
    const rightAttention = attentionByShipmentId[right.id]
    const leftBucket = getShipmentPriorityBucket(left, leftAttention)
    const rightBucket = getShipmentPriorityBucket(right, rightAttention)

    if (leftBucket !== rightBucket) {
      return leftBucket - rightBucket
    }

    if (leftAttention || rightAttention) {
      const leftPriority = leftAttention?.priority ?? Number.MAX_SAFE_INTEGER
      const rightPriority = rightAttention?.priority ?? Number.MAX_SAFE_INTEGER
      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority
      }
    }

    return getShipmentActivityTimestamp(right, trackingByShipment) - getShipmentActivityTimestamp(left, trackingByShipment)
  })
}

export function countShipmentsStartingToday(shipments: Shipment[], now: Date) {
  return shipments.filter((shipment) => {
    const loadingDate = getShipmentCalendarDates(shipment).loadingDate
    if (!loadingDate) return false
    return new Date(loadingDate).toDateString() === now.toDateString()
  }).length
}

function msUntil(timestamp: string | undefined, now: Date) {
  const time = timestamp ? new Date(timestamp).getTime() : Number.NaN
  return Number.isNaN(time) ? 0 : time - now.getTime()
}



// «25 мин», «3 ч», «3 ч 10 мин» — the server's HoursMinutesText.
function formatSpan(ms: number) {
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return 'меньше минуты'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m} мин`
  return m === 0 ? `${h} ч` : `${h} ч ${m} мин`
}

// «с 14:20», or «с 25.10, 14:20» when it was not today.
function formatSince(timestamp: string, now: Date) {
  const at = new Date(timestamp)
  const time = at.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  if (at.toDateString() === now.toDateString()) return `с ${time}`
  return `с ${at.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}, ${time}`
}

// Lateness says how far behind the plan it is — a fact about time, true wherever the truck is.
// Standing says when it began, not how long it lasts: with the GPS gone quiet nobody knows that.
function withLate(label: string, plannedAt: string | undefined, now: Date) {
  return plannedAt ? `${label} · ${formatSpan(-msUntil(plannedAt, now))}` : label
}

function withSince(label: string, since: string | undefined, now: Date) {
  return since ? `${label} · ${formatSince(since, now)}` : label
}

// The name of a lateness task. Before the trip started there is no truck to be late — nobody took the
// trip — so it says what is known, as the server's push does: the time passed.
export function getLateTaskTitle(code: string, statusCode: string): string | undefined {
  const started = statusCode === 'in_progress'
  // Accepted, «Начать рейс» not pressed: the driver is there, the trip is not (26.10.2026).
  if (code === 'late_loading' && statusCode === 'accepted') return 'Время погрузки прошло, рейс не начат'
  if (code === 'late_loading') return started ? 'Опоздание на погрузку' : 'Время погрузки прошло'
  if (code === 'late_unloading') return started ? 'Опоздание на выгрузку' : 'Время выгрузки прошло'
  return undefined
}

// The words for a server task. Only wording lives here — the rule that raised it is on the server.
export function describeControlTask(task: ManagerControlTask, now: Date): string {
  switch (task.code) {
    case 'completion_requested':
      return `Водитель просит завершить заявку. ${describeAutoCompletion(msUntil(task.completion_due_at, now))}`
    case 'missing_documents':
      // Not attention yet: the driver's two hours after finishing are still running.
      return task.attention ? 'Документы не загружены' : 'Ожидаются документы'
    case 'missing_assignment':
      return 'Не назначены машина и водитель'
    case 'missing_vehicle':
      return 'Не назначена машина'
    case 'missing_driver':
      return 'Не назначен водитель'
    case 'driver_rejected':
      return 'Водитель отказался — пригласите другого'
    case 'no_geolocation':
      return 'Геопозиция не поступала'
    case 'stale_geolocation':
      return task.last_fix_at
        ? `Нет свежей геопозиции ${formatHoursSince(task.last_fix_at, now).replace(' назад', '')}`
        : 'Нет свежей геопозиции'
    case 'late_loading':
    case 'late_unloading':
      return withLate(getLateTaskTitle(task.code, task.status_code)!, task.planned_at, now)
    case 'long_stop':
      return withSince('Долгая стоянка в пути', task.standing_since, now)
    case 'long_stay_loading':
      return withSince('Долгая стоянка на погрузке', task.standing_since, now)
    case 'long_stay_unloading':
      return withSince('Долгая стоянка на выгрузке', task.standing_since, now)
    case 'needs_review':
      return 'Заявке больше 72 часов'
    default:
      return 'Требует внимания'
  }
}

// The line under a reason in the shipment: what closes it. Lateness and standing close themselves once
// the GPS confirms the truck moved on — the same rule as the server's.
export function describeControlTaskHint(task: Pick<ManagerTaskItem, 'code' | 'canBeManuallyResolved' | 'statusCode'>): string {
  if (task.canBeManuallyResolved) return 'Если вопрос уже решён, эту причину можно закрыть вручную.'
  if ((task.code === 'late_loading' || task.code === 'late_unloading') && task.statusCode === 'accepted') {
    return 'Водитель принял заявку, но не начал рейс: попросите его нажать «Начать рейс» или поправьте время в заявке.'
  }
  if ((task.code === 'late_loading' || task.code === 'late_unloading') && task.statusCode !== 'in_progress') {
    return 'Водитель ещё не в пути: пригласите водителя или поправьте время в заявке.'
  }
  switch (task.code) {
    case 'late_loading':
      return 'Снимется, когда GPS подтвердит прибытие на погрузку. Если время изменилось — поправьте его в заявке.'
    case 'late_unloading':
      return 'Снимется, когда GPS подтвердит прибытие на выгрузку. Если время изменилось — поправьте его в заявке.'
    case 'long_stop':
      return 'Снимется, когда GPS подтвердит, что машина снова в пути.'
    case 'long_stay_loading':
      return 'Снимется, когда GPS подтвердит выезд с погрузки.'
    case 'long_stay_unloading':
      return 'Снимется, когда GPS подтвердит выезд с выгрузки.'
    default:
      return 'Эту причину нельзя закрыть вручную — сначала исправьте ситуацию в заявке.'
  }
}

export function toManagerTaskItem(task: ManagerControlTask, now: Date): ManagerTaskItem {
  return {
    id: `${task.shipment_id}:${task.code}`,
    shipmentId: task.shipment_id,
    code: task.code,
    title: task.title,
    route: task.route,
    statusCode: task.status_code,
    status: getManagerStatusLabel(task.status_code),
    reason: describeControlTask(task, now),
    priority: task.priority,
    priorityRank: task.rank,
    createdAt: task.shipment_created_at,
    canBeManuallyResolved: task.can_be_manually_resolved,
    attention: task.attention,
    control: task.control ?? task.attention,
  }
}

// The control queue in the server's order (rank, then newer shipments first).
export function buildManagerTaskItems(tasks: ManagerControlTask[], now: Date): ManagerTaskItem[] {
  return tasks.map((task) => toManagerTaskItem(task, now))
}

// Lifecycle and attention are independent. Missing assignment alone is normal waiting;
// refusal, overdue waiting, documents and tracking problems require action on any stage.
// Keep this policy aligned with mobile/src/domain/managerTasks.ts (cross-client tests cover it).
export function getManagerStatusLabel(status: string, assignment?: { status: string; driver_id?: string | null } | null) {
  return getWaitingStatusLabel(status, assignment?.status === 'pending' && Boolean(assignment.driver_id)) ?? translateStatus(status)
}

export function isAwaitingAssignmentTask(task: Pick<ManagerTaskItem, 'statusCode'>) {
  return task.statusCode === 'created' || task.statusCode === 'awaiting_driver' || task.statusCode === 'driver_rejected' || task.statusCode === 'pending' || task.statusCode === 'assigned'
}

// The neutral line of a shipment finished a moment ago without papers: the server's documents_pending
// before its missing_documents reason (2 h later) — waiting, not a problem yet. Undefined otherwise.
// Keep the words aligned with mobile/src/domain/managerTasks.ts.
export const DOCUMENTS_AWAITED_NOTE = 'Ожидаются документы — у водителя есть 2 часа после завершения, чтобы их отправить'

export function getDocumentsAwaitedNote(shipment: Pick<Shipment, 'operational'>): string | undefined {
  const op = shipment.operational
  if (!op?.documents_pending) return undefined
  return op.control_tasks.some((task) => task.code === 'missing_documents') ? undefined : DOCUMENTS_AWAITED_NOTE
}

export function isDocumentsTask(task: Pick<ManagerTaskItem, 'code'>) {
  return task.code === 'missing_documents'
}

function firstTaskForEachShipment(tasks: ManagerTaskItem[]) {
  const shipmentIds = new Set<string>()
  return tasks.filter((task) => {
    if (shipmentIds.has(task.shipmentId)) return false
    shipmentIds.add(task.shipmentId)
    return true
  })
}

// The server order is already priority-first. The first reason is enough for a summary card;
// the full list remains visible in the shipment itself.
export function getPrimaryAttentionTasks(tasks: ManagerTaskItem[]) {
  return firstTaskForEachShipment(getAttentionTasks(tasks))
}

// The same for «Контроль перевозок»: also a finished shipment still waiting for its documents.
export function getPrimaryControlTasks(tasks: ManagerTaskItem[]) {
  return firstTaskForEachShipment(getControlTasks(tasks))
}

export function getPrimaryDocumentTasks(tasks: ManagerTaskItem[]) {
  return firstTaskForEachShipment(tasks.filter(isDocumentsTask))
}

// Every task of the given shipments, in the server's order — for screens that already hold the
// shipments and need no separate summary request. Inside one shipment the server's order stands (the
// sort is stable): it puts a GPS problem before the stops read off the GPS.
export function collectShipmentTasks(shipments: Shipment[]): ManagerControlTask[] {
  return shipments
    .flatMap((shipment) => getShipmentControlTasks(shipment))
    .sort((a, b) => a.rank - b.rank
      || new Date(b.shipment_created_at).getTime() - new Date(a.shipment_created_at).getTime()
      || a.shipment_id.localeCompare(b.shipment_id))
}

// A shipment's most urgent task as a card highlight. The server sends control_tasks already
// sorted, so the first one is the primary reason.
export function getShipmentAttention(shipment: Shipment, now: Date, scope: 'presentation' | 'registry' | 'tracking' = 'presentation'): AttentionItem | undefined {
  const tasks = buildManagerTaskItems(getShipmentControlTasks(shipment), now)
    .filter((task) => scope !== 'tracking' || isOperationalTask(task))
  const task = (scope === 'registry' ? [...tasks].sort((a, b) => a.priorityRank - b.priorityRank) : getAttentionTasks(tasks))[0]
  if (!task) return undefined
  return {
    id: shipment.id,
    code: task.code,
    title: task.title,
    route: task.route,
    statusCode: task.statusCode,
    status: getManagerStatusLabel(task.statusCode, shipment.assignment),
    reason: scope === 'registry' ? `${task.reason}${tasks.length > 1 ? ` · ещё ${tasks.length - 1}` : ''}` : getAttentionLabel(tasks)!,
    priority: task.priorityRank,
    createdAt: task.createdAt,
  }
}

export function buildAttentionByShipmentId(shipments: Shipment[], now: Date, scope: 'presentation' | 'registry' | 'tracking' = 'presentation'): Record<string, AttentionItem> {
  const result: Record<string, AttentionItem> = {}
  for (const shipment of shipments) {
    const item = getShipmentAttention(shipment, now, scope)
    if (item) result[shipment.id] = item
  }
  return result
}
